import type { Country } from 'react-phone-number-input'
import { DEFAULT_COUNTRY, PhoneInput } from '@/components/erp/phone-input'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft, ArrowRight, CloudOff, Loader2, RefreshCw, Send, Trash2, UploadCloud, WifiOff,
} from 'lucide-react'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { Field } from '@/shared/ui/Modal'
import { useCan } from '@/shared/auth/session'
import { languageLabel } from '@/shared/lib/language'
import { fmtDateTime } from '@/shared/lib/format'
import type { RespondentLookupDto, SurveyQuestionDto } from '@/shared/api/types'
import { explain } from '@/shared/api/problem'
import { lookUpRespondent } from './api'
import {
  abandon, applyIdentity, backToQuestions, consentScript, giveConsent, isAnswered, locate,
  nextQuestion, previousQuestion, questionHelp, questionText, refuseConsent, retrySubmit,
  setConsentMethod, setDetails, setLanguage, startConsentRecording, startNextInterview,
  stopConsentRecording, submit, answer, type CaptureSnapshot,
} from './captureSession'
import {
  useCaptureQueue, useCaptureSession, useCompleteness, useElapsedSince, useOpenCapture, useWalk,
} from './useCapture'
import { AudioRecorder, RecordingBeacon, startRecording, stopRecording, useRecorder }
  from './AudioRecorder'
import { discardRejected, drainQueue, retryRejected } from './captureQueue'
import { QuestionInput } from './QuestionInput'
import { CaptureSummary } from './CaptureSummary'
import { Pill, count, questionTypeLabel, ageBandLabel } from './vocab'

/**
 * The interview capture screen.
 *
 * ## This is the first screen in the product used under time pressure
 *
 * Everything else in the Polling Studio is read at a desk by somebody who can
 * take a second look. This one is used standing in a doorway, on a phone, in
 * sunlight, with a person waiting and possibly no signal. Three things follow
 * from that and they explain most of the layout:
 *
 * - **One question at a time**, full width, with targets a thumb can hit. A
 *   scrolling form of twenty questions is faster on a laptop and produces
 *   mis-taps in the field.
 * - **Nothing is hidden behind a menu.** The queue depth, whether the device is
 *   online, and whether the microphone is on are all permanently visible,
 *   because each of them is a thing an interviewer would otherwise discover at
 *   the end of the day.
 * - **The interview is not in this component.** It lives in `captureSession`, at
 *   module scope. Navigating away, remounting, or a stray re-render cannot end
 *   it, cannot restart it and cannot mint a second idempotency key. That is not
 *   caution; this project already shipped a live camera inside a `useEffect` and
 *   took a broadcast off air by navigating.
 *
 * ## What the interviewer is never asked to decide
 *
 * Which unit the interview belongs to (it is the assignment's), whether to
 * retry after a failure (the key makes a retry safe, so it is just offered), and
 * whether a refusal is worth recording (it is — it is the denominator of the
 * cooperation rate).
 */
export function CapturePage() {
  const canField = useCan('survey:field')
  const [params] = useSearchParams()
  const surveyId = params.get('poll')
  const assignmentId = params.get('assignment')

  useOpenCapture(surveyId, assignmentId)
  const session = useCaptureSession()
  const queue = useCaptureQueue()
  const rec = useRecorder()

  if (!canField) return <Forbidden what="the interview capture screen" />

  if (!surveyId) {
    return (
      <Panel title="No poll named">
        <Note tone="info">
          This screen needs a poll to field. Open it from <Link className="erp-link" to="/polling/my-work">My work</Link>,
          which passes the poll and the assignment the interview should be attributed to.
        </Note>
      </Panel>
    )
  }

  return (
    <div className="space-y-4">
      <StickyStatus
        session={session}
        pending={queue.pending.length}
        clips={queue.waitingClips.reduce((n, w) => n + w.count, 0)}
        online={queue.online}
      />

      {session.phase === 'loading' && !session.bundle && (
        <LoadingBlock label="Loading the instrument…" />
      )}

      {session.phase === 'unavailable' && (
        <Panel title="This poll cannot be fielded right now">
          <div className="space-y-3">
            <Note tone="bad">{session.loadError ?? 'The instrument could not be loaded.'}</Note>
            <p className="text-erp-xs text-muted-foreground">
              Nothing has been lost. If this device has fielded this poll before, its instrument
              is cached and this screen will use it as soon as the network answers or fails —
              this message means there is no cached copy either.
            </p>
            <Link to="/polling/my-work" className="erp-btn h-11">Back to my work</Link>
          </div>
        </Panel>
      )}

      {session.bundle && (
        <>
          {session.fromCache && (
            <Note tone="info">
              Working from the copy of this instrument saved on this device
              {session.cachedAt ? ` on ${fmtDateTime(session.cachedAt)}` : ''}. The questions are
              the ones that were published then. If the poll has been paused or re-published
              since, this device does not know it yet — and the server will refuse an interview
              against a poll that is no longer live.
            </Note>
          )}

          {session.phase === 'consent' && <ConsentStep session={session} />}
          {session.phase === 'interview' && <InterviewStep session={session} />}
          {(session.phase === 'details'
            || (session.phase === 'submitting' && session.consentGiven !== false))
            && <DetailsStep session={session} />}
          {/*
            A refusal submits straight from the consent step, so there is no
            details form behind it to show while it is in flight.
          */}
          {session.phase === 'submitting' && session.consentGiven === false && (
            <LoadingBlock label="Recording the refusal…" />
          )}
          {session.phase === 'done' && (
            <CaptureSummary session={session} onNext={startNextInterview} />
          )}
        </>
      )}

      {rec.phase === 'off' && session.phase !== 'done' && session.bundle
        && session.bundle.instrument.audioRecordingMode !== 'OFF'
        && rec.clips.length === 0 && session.phase !== 'consent' && (
        <Note tone="bad">
          Nothing is being recorded. This poll expects audio
          {session.bundle.instrument.audioRecordingMode === 'REQUIRED'
            ? ' and will not accept a COMPLETE interview without it'
            : ''}. Start it from the Recording panel before going further.
        </Note>
      )}

      <QueuePanel />

    </div>
  )
}

// ── the permanent status line ───────────────────────────────────────────────

/**
 * Always visible, never a menu.
 *
 * The recording beacon, the connection and the queue depth are the three facts
 * an interviewer needs continuously and would otherwise learn too late. Sticky
 * because the question below it scrolls and these do not stop being true.
 */
function StickyStatus({
  session, pending, clips, online,
}: { session: CaptureSnapshot; pending: number; clips: number; online: boolean }) {
  const elapsed = useElapsedSince(session.startedAt)
  const b = session.bundle
  return (
    <div className="sticky top-0 z-30 -mx-4 border-b border-border bg-background/95 px-4 py-2 backdrop-blur space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-erp-md font-semibold tracking-tight truncate">
          {b?.surveyRef ?? 'Interview'}
        </span>
        <span className="text-erp-xs text-muted-foreground truncate min-w-0 flex-1">
          {b?.surveyTitle}
        </span>
        {!online && (
          <Pill tone="bg-amber-500/10 text-amber-700 border-amber-500/30">
            <WifiOff className="mr-1 h-3 w-3" /> offline
          </Pill>
        )}
        {pending > 0 && (
          <Pill tone="bg-sky-500/10 text-sky-700 border-sky-500/30">
            <CloudOff className="mr-1 h-3 w-3" /> {pending} waiting
          </Pill>
        )}
        {/*
          Its own pill, never added to the one above. A recording still going up
          belongs to an interview the server already has, and counting the two
          together would read as unsent interviews that do not exist.
        */}
        {clips > 0 && (
          <Pill tone="bg-violet-500/10 text-violet-700 border-violet-500/30">
            <UploadCloud className="mr-1 h-3 w-3" /> {clips} recording{clips === 1 ? '' : 's'}
          </Pill>
        )}
        {session.startedAt && session.phase !== 'done' && (
          <span className="tabular-nums font-mono text-erp-sm text-muted-foreground">
            {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}
          </span>
        )}
      </div>
      <RecordingBeacon />
    </div>
  )
}

// ── consent ─────────────────────────────────────────────────────────────────

const CONSENT_METHODS: { code: string; label: string; hint: string }[] = [
  {
    code: 'VERBAL_ATTESTED',
    label: 'Verbal',
    hint: 'They said yes and you are attesting to it. The usual case.',
  },
  {
    code: 'VERBAL_RECORDED',
    label: 'Verbal, recorded',
    hint: 'Their agreement is captured as its own clip, separate from the interview.',
  },
  { code: 'WRITTEN', label: 'Written', hint: 'They signed a form you are carrying.' },
  { code: 'DIGITAL', label: 'Digital', hint: 'They consented on a device.' },
]

/**
 * First, and not skippable.
 *
 * Recording starts AFTER this step and never before — a clip that begins while
 * the script is still being read is a recording nobody has agreed to, and the
 * audio disclosure in the script is the thing that makes the rest lawful.
 *
 * `VERBAL_RECORDED` produces a SEPARATE `AUDIO_CONSENT` clip. Two files rather
 * than one so that verifying consent does not require listening to the
 * interview — those are different disclosures, and the second needs
 * `survey:listen`.
 */
function ConsentStep({ session }: { session: CaptureSnapshot }) {
  const bundle = session.bundle!
  const script = consentScript(bundle, session.languageCode)
  const rec = useRecorder()
  const mode = bundle.instrument.audioRecordingMode
  const recorded = session.consentMethodCode === 'VERBAL_RECORDED'
  const consentClip = rec.clips.find((c) => c.kindCode === 'AUDIO_CONSENT')
  const [busy, setBusy] = useState(false)

  async function proceed() {
    setBusy(true)
    await giveConsent()
    if (recorded && mode !== 'OFF') await startRecording('AUDIO_INTERVIEW')
    setBusy(false)
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Consent"
        description="Read it out. Nothing is asked and nothing is recorded until this is answered."
      />

      <Panel title="Language" description="The script and every question are read in this one.">
        <div className="flex flex-wrap gap-2">
          {bundle.instrument.languages.map((code) => (
            <button
              key={code}
              type="button"
              aria-pressed={session.languageCode === code}
              onClick={() => setLanguage(code)}
              className={`erp-btn h-11 ${session.languageCode === code ? 'erp-btn-primary' : ''}`}
            >
              {/* An interviewer reading a script off a phone picks the language by its
                  NAME. "SW" is a thing you decode; "Kiswahili" is a thing you read. */}
              {languageLabel(code)}
            </button>
          ))}
        </div>
      </Panel>

      <Panel title="The consent statement">
        {script ? (
          <div className="space-y-3">
            <p className="whitespace-pre-wrap text-erp-md leading-relaxed">{script.body}</p>
            {script.audioDisclosure && mode !== 'OFF' && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
                <p className="text-erp-xs uppercase tracking-wide text-amber-800">
                  Read this too — the recording depends on it
                </p>
                <p className="mt-1 whitespace-pre-wrap text-erp-md leading-relaxed text-amber-900">
                  {script.audioDisclosure}
                </p>
              </div>
            )}
          </div>
        ) : (
          <Note tone="bad">
            This poll has no consent script in {session.languageCode.toUpperCase()}. A poll
            cannot go live without one in every declared language, so either the language is
            wrong or the instrument changed after this device cached it. Pick another language
            or reload with signal.
          </Note>
        )}
      </Panel>

      <Panel title="How was consent given?">
        <div className="grid gap-2">
          {CONSENT_METHODS.map((m) => (
            <button
              key={m.code}
              type="button"
              aria-pressed={session.consentMethodCode === m.code}
              onClick={() => setConsentMethod(m.code)}
              className={`rounded-md border px-3 py-3 text-left min-h-[52px] ${
                session.consentMethodCode === m.code
                  ? 'border-chocolate bg-chocolate-soft/40'
                  : 'border-border bg-background hover:bg-muted/40'}`}
            >
              <span className="block text-erp-md font-medium">{m.label}</span>
              <span className="block text-erp-xs text-muted-foreground">{m.hint}</span>
            </button>
          ))}
        </div>
      </Panel>

      {recorded && mode !== 'OFF' && (
        <Panel title="Consent recording">
          <AudioRecorder
            mode={mode}
            kindCode="AUDIO_CONSENT"
            responseId={null}
            title="The consent statement, as its own clip"
            why={'Recorded separately from the interview so somebody can verify the consent was '
              + 'obtained without listening to what the respondent then said.'}
            onStart={() => { void startConsentRecording() }}
            onStop={() => { void stopConsentRecording() }}
            busy={busy}
          />
        </Panel>
      )}

      <Panel>
        <div className="space-y-3">
          <button
            type="button"
            className="erp-btn erp-btn-primary h-14 w-full text-erp-lg"
            disabled={busy || (recorded && mode !== 'OFF' && !consentClip)}
            onClick={() => { void proceed() }}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            They agreed — start the interview
          </button>
          {recorded && mode !== 'OFF' && !consentClip && (
            <p className="text-erp-xs text-muted-foreground">
              Record the consent statement first. You chose “verbal, recorded”, and that choice
              is a claim that a clip exists.
            </p>
          )}

          <button
            type="button"
            className="erp-btn h-14 w-full border-red-300 text-red-800 hover:bg-red-50"
            disabled={busy}
            onClick={() => { void refuseConsent() }}
          >
            They declined
          </button>
          {/*
            Said out loud because the instinct is to close the tab. A refusal is
            the denominator of the cooperation rate; an abandoned form is a door
            that looks like it was never knocked on.
          */}
          <p className="text-erp-xs text-muted-foreground">
            A refusal is DATA. It is submitted as a REFUSAL disposition, not thrown away — it is
            what tells a supervisor this area is hostile rather than unvisited, and it is half
            of every response rate this poll publishes.
          </p>
        </div>
      </Panel>
    </div>
  )
}

// ── the questions ───────────────────────────────────────────────────────────

function InterviewStep({ session }: { session: CaptureSnapshot }) {
  const walkOutcome = useWalk(session)
  const bundle = session.bundle!
  const visible = walkOutcome.visible
  const index = Math.min(session.cursor, Math.max(0, visible.length - 1))
  const question: SurveyQuestionDto | undefined = visible[index]

  if (visible.length === 0 || !question) {
    return (
      <Panel title="No questions to ask">
        <div className="space-y-3">
          <Note tone="info">
            Every question in this instrument is hidden by a branch for the answers given so
            far. That is the instrument working, not a fault — go on to the respondent's
            details and submit.
          </Note>
          <button type="button" className="erp-btn erp-btn-primary h-12"
            onClick={() => backToQuestions()}>
            Continue
          </button>
        </div>
      </Panel>
    )
  }

  const value = session.answers[question.id]
  const answered = isAnswered(value)
  const help = questionHelp(question, session.languageCode)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-erp-xs uppercase tracking-wide text-muted-foreground">
          Question {index + 1} of {visible.length}
        </span>
        <span className="h-1.5 flex-1 overflow-hidden rounded bg-muted">
          <span className="block h-full bg-chocolate"
            style={{ width: `${((index + 1) / visible.length) * 100}%` }} />
        </span>
        <Pill>{questionTypeLabel(question.typeCode)}</Pill>
      </div>

      <Panel>
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="text-erp-xl font-medium leading-snug">
              {questionText(question, session.languageCode)}
            </p>
            {help && <p className="text-erp-sm text-muted-foreground">{help}</p>}
            {question.required && !answered && (
              <p className="text-erp-xs text-amber-700">
                Required. An interview missing it is submitted as PARTIAL rather than COMPLETE.
              </p>
            )}
          </div>

          {/*
            Keyed by the question. The walk renders one question at a time into
            the same tree position, so without this React reuses the instance
            across two questions of the same type and their local state comes
            with it — a refused location fix on question 5 showing its error
            under question 6, and question 6 reporting question 5's accuracy.
          */}
          <QuestionInput
            key={question.id}
            question={question}
            all={walkOutcome.all}
            language={session.languageCode}
            seed={session.optionOrderSeed}
            randomiseGlobally={bundle.instrument.randomiseOptionOrder}
            answers={session.answers}
            hiddenBy={walkOutcome.hiddenBy}
            onChange={answer}
          />
        </div>
      </Panel>

      {walkOutcome.ended && (
        <Note tone="info">
          A branch has ended the interview here. The remaining questions are not blank — they
          were never asked, and the submission records that difference.
        </Note>
      )}

      <div className="flex gap-2">
        <button type="button" className="erp-btn h-14 flex-1" onClick={previousQuestion}
          disabled={index === 0 && !session.consentRequired}>
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button type="button" className="erp-btn erp-btn-primary h-14 flex-[2]"
          onClick={nextQuestion}>
          {index + 1 >= visible.length ? 'Respondent details' : 'Next'}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
      {/*
        Next is never blocked on an answer. A respondent who will not answer a
        required question has still given every other answer, and losing the
        interview to enforce one field is a worse outcome than a PARTIAL — which
        is exactly what the disposition code is for.
      */}
      {question.required && !answered && (
        <p className="text-erp-xs text-muted-foreground">
          You can move on without this. It is recorded as shown-and-left-blank, which is a
          different thing from a question the branches never asked.
        </p>
      )}
    </div>
  )
}

// ── respondent details and submit ───────────────────────────────────────────

/**
 * The last screen before the interview leaves the device.
 *
 * Everything here is optional except the unit, which is the assignment's and is
 * not editable: an interview attributed to a ward nobody was sent to is worse
 * than one with no name on it.
 *
 * The age is asked in YEARS because that is the field
 * `CaptureRequests.ResponseSubmission` carries — `respondentAge: Short`. There is
 * no age-band field on the wire; the band is derived server-side and appears on
 * the read model as `respondentAgeBand`. Offering a band picker here would mean
 * inventing a year to send, which would be a number nobody said. The bands from
 * `getRegisterVocabulary()` are shown instead as the quota context, so the
 * interviewer can see who the sample still needs before spending twenty minutes.
 */
/**
 * The doorstep identity check (backend prompt 64 §1).
 *
 * ## What it saves
 *
 * The respondent hands over an ID. The register is asked first and eCitizen
 * second, and the name, gender and year of birth stop being three questions
 * somebody has already answered on a government form. The YEAR comes back and
 * this turns it into years of age — the conversion happens once, here, rather
 * than a birth year being typed into a box labelled "Age in years".
 *
 * ## What it will not do
 *
 * **It does not lock the fields.** Every prefilled value stays editable, and
 * editing one takes the interview back to `MANUAL` — see `setDetails`. The
 * register knows who was registered at an address; the interviewer is looking at
 * the person. When those disagree the person wins, and the recorded source says
 * which it was.
 *
 * **A register match brings no phone number.** Contact channels are sealed
 * behind `voter:read-pii` and an audited reveal. An eCitizen match does bring
 * one, and it is prefilled precisely so it can be corrected.
 */
function IdentityLookup({ session }: { session: CaptureSnapshot }) {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<RespondentLookupDto | null>(null)
  const [error, setError] = useState<string | null>(null)
  const id = session.details.nationalId.trim()
  const source = session.details.identitySourceCode

  async function look() {
    if (!id || !session.surveyId) return
    setBusy(true)
    setError(null)
    try {
      const match = await lookUpRespondent(session.surveyId, id)
      setResult(match)
      if (match.found) applyIdentity(match)
    } catch (e) {
      // A failed lookup is never a failed interview. The details are typed and
      // the interview proceeds — which is also what a "not found" means.
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-md border border-border p-3 space-y-2">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[180px] flex-1">
          <Field label="National ID"
            hint="Optional. If they show one, this fills in the rest.">
            <input className="erp-input w-full h-12 tabular-nums" inputMode="numeric"
              value={session.details.nationalId}
              onChange={(e) => setDetails({ nationalId: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void look() } }} />
          </Field>
        </div>
        <button type="button" className="erp-btn h-12 text-erp-sm shrink-0"
          disabled={!id || busy} onClick={() => { void look() }}>
          {busy ? 'Checking…' : 'Look up'}
        </button>
      </div>

      {error && <Note tone="bad">{error}</Note>}

      {result && !error && (
        <Note tone={result.found ? 'good' : 'info'}>
          {result.sentence}
          {result.found && result.registrationStatusCode && (
            <span className="mt-1 block text-erp-xs">
              Registration status: {result.registrationStatusCode}.
            </span>
          )}
        </Note>
      )}

      {source !== 'MANUAL' && (
        <p className="text-erp-xs text-muted-foreground">
          The details below came from{' '}
          {source === 'LOCAL_REGISTER' ? 'the voter register' : 'eCitizen'}. Change any of them
          if the person in front of you says otherwise — the interview then records that you
          entered them, not that a register did.
        </p>
      )}
    </div>
  )
}

/**
 * The age field's own complaint, before the interview is submitted.
 *
 * ## Why this exists
 *
 * A birth year typed into a field labelled "Age in years" is the mistake that
 * actually happens — 1998 went in, `ck_survey_response__age` refused it in
 * Postgres, and the interviewer got a 500 naming a database constraint AFTER
 * conducting the interview. The server now answers that with a sentence, which
 * is the real fix; this one arrives while the number is still being typed,
 * because on this screen a round trip costs a doorstep.
 *
 * It does NOT block the submit. The server owns the refusal — see the module
 * note on why a control the caller might be allowed to use is offered — and a
 * client that clamped 1998 to 130 would be inventing a respondent's age.
 */
function ageComplaint(raw: string): string | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const age = Number(trimmed)
  if (!Number.isFinite(age)) return 'That is not a number.'
  if (age >= 1900) {
    const year = new Date().getFullYear()
    return `That looks like a year of birth. Somebody born in ${trimmed} is about `
      + `${year - Math.round(age)} — this field wants the age.`
  }
  if (age < 0 || age > 130) return 'An age has to be between 0 and 130.'
  return null
}

function DetailsStep({ session }: { session: CaptureSnapshot }) {
  const bundle = session.bundle!
  const rec = useRecorder()
  const done = useCompleteness(session, rec.clips.length)
  const mode = bundle.instrument.audioRecordingMode
  const genders = session.register.gender ?? []
  const bands = session.register.ageBand ?? []
  const busy = session.phase === 'submitting'

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Respondent"
        description="All optional except the unit. Ask for what they will give and no more."
      />

      {session.refusal && (
        <Note tone="bad">
          {session.refusal}
          <span className="mt-1 block text-erp-xs">
            Nothing has been lost and nothing has been submitted twice — this interview keeps the
            key it was given when it started, so pressing Submit again cannot create a second
            respondent.
          </span>
        </Note>
      )}

      <Panel title="Details">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12">
            <IdentityLookup session={session} />
          </div>

          <div className="col-span-12 sm:col-span-6">
            <Field label="Name" hint="Masked on every read. Blank is fine.">
              <input className="erp-input w-full h-12" value={session.details.name}
                onChange={(e) => setDetails({ name: e.target.value })} />
            </Field>
          </div>
          <div className="col-span-12 sm:col-span-6">
            <Field label="Phone" hint="Used for back-checks and duplicate detection.">
              {/* The picker, not a bare text box: the server validates the number against
                  the COUNTRY's numbering plan, and without one it can only normalise. */}
              <PhoneInput
                value={session.details.phone}
                onChange={(e164) => setDetails({ phone: e164 })}
                country={(session.details.phoneCountry || DEFAULT_COUNTRY) as Country}
                onCountryChange={(c) => setDetails({ phoneCountry: c })}
              />
            </Field>
          </div>

          <div className="col-span-12">
            <p className="mb-1.5 text-erp-xs font-medium text-muted-foreground">Gender</p>
            {genders.length === 0 ? (
              <p className="text-erp-xs text-muted-foreground">
                The register's gender list could not be read from the server, so nothing is
                offered here rather than a hand-written list that would not match it.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {genders.map((code) => (
                  <button key={code} type="button"
                    aria-pressed={session.details.gender === code}
                    onClick={() => setDetails({
                      gender: session.details.gender === code ? '' : code,
                    })}
                    className={`erp-btn h-12 ${
                      session.details.gender === code ? 'erp-btn-primary' : ''}`}>
                    {humanise(code)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="col-span-12 sm:col-span-4">
            <Field label="Age in years"
              hint="The band is worked out from this, server-side."
              error={ageComplaint(session.details.age) ?? undefined}>
              {/*
                `max` and `min` are the browser's hint; the sentence under the
                field is what actually gets read. Neither BLOCKS the submit —
                the server owns the refusal, and a client that silently clamped
                the number would be inventing a respondent's age.
              */}
              <input className="erp-input w-full h-12 tabular-nums" inputMode="numeric"
                type="number" min={0} max={130}
                value={session.details.age}
                onChange={(e) => setDetails({ age: e.target.value })} />
            </Field>
          </div>
          <div className="col-span-12 sm:col-span-8">
            <p className="mb-1.5 text-erp-xs font-medium text-muted-foreground">
              Bands this poll counts in
            </p>
            <div className="flex flex-wrap gap-1.5">
              {bands.length === 0
                ? <span className="text-erp-xs text-muted-foreground">—</span>
                // `ageBandLabel`, not a local `humanise` — the quota panel three
                // rows below already renders these codes through it, and two
                // spellings of the same band on one screen ("18–24" beside
                // "18 24") reads as two different bands.
                : bands.map((b) => <Pill key={b}>{ageBandLabel(b)}</Pill>)}
            </div>
          </div>

          <div className="col-span-12">
            <Field label="Unit" hint="From your assignment. It is what the estimate is projected to.">
              <input className="erp-input w-full h-12 bg-muted/40" readOnly
                value={session.details.unitName ?? session.details.unitId} />
            </Field>
          </div>
        </div>
      </Panel>

      <QuotaPanel session={session} />

      {mode !== 'OFF' && (
        <Panel title="Recording">
          <AudioRecorder
            mode={mode}
            kindCode="AUDIO_INTERVIEW"
            responseId={session.result?.responseId ?? null}
            title="The interview"
            why={mode === 'REQUIRED'
              ? 'This poll requires a recording, so a COMPLETE interview needs one. Without it '
                + 'the interview is still submitted — as PARTIAL.'
              : 'Optional on this poll. A recording is what a back-check listens to.'}
            onStart={() => { void startRecording('AUDIO_INTERVIEW') }}
            onStop={() => { void stopRecording() }}
            busy={busy}
          />
        </Panel>
      )}

      <Panel title="Where this was taken">
        <div className="space-y-2">
          {session.geoState === 'asking' && (
            <p className="text-erp-sm text-muted-foreground">
              <Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" />
              Waiting for a fix. It can take twenty seconds outdoors and longer indoors.
            </p>
          )}
          {session.place?.ok && (
            <p className="text-erp-sm tabular-nums">
              {session.place.location.latitude.toFixed(5)},
              {' '}{session.place.location.longitude.toFixed(5)}
              <span className="ml-2 text-erp-xs text-muted-foreground">
                ±{session.place.location.accuracyM ?? '—'} m
              </span>
            </p>
          )}
          {session.place && !session.place.ok && (
            <Note tone="info">
              {session.place.reason} This does not stop the interview. It is submitted without a
              fix and the server records GPS_MISSING, which is the honest outcome — a
              coordinate typed in to satisfy a form would not be.
            </Note>
          )}
          <button type="button" className="erp-btn h-11"
            disabled={session.geoState === 'asking'} onClick={() => { void locate() }}>
            <RefreshCw className="h-3.5 w-3.5" /> Try for a fix again
          </button>
        </div>
      </Panel>

      <Panel title="Before you submit">
        <div className="space-y-3">
          <p className="text-erp-sm">
            {count(done.answered)} answered
            {done.missingRequired.length > 0
              ? ` · ${done.missingRequired.length} required question${
                done.missingRequired.length === 1 ? '' : 's'} left blank`
              : ' · every required question answered'}
          </p>
          {done.audioMissing && (
            <Note tone="bad">
              This poll requires a recording and none has been made. The interview can still be
              submitted — it will be a PARTIAL, and the server will raise AUDIO_MISSING against
              it, which is a supervisor's question rather than a rejection.
            </Note>
          )}
          <p className="text-erp-xs text-muted-foreground">
            This will be submitted as <strong>{done.complete ? 'COMPLETE' : 'PARTIAL'}</strong>.
          </p>

          <button type="button" className="erp-btn erp-btn-primary h-14 w-full text-erp-lg"
            disabled={busy} onClick={() => { void submit() }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Submit the interview
          </button>
          {session.refusal && (
            <button type="button" className="erp-btn h-12 w-full" disabled={busy}
              onClick={() => { void retrySubmit() }}>
              <RefreshCw className="h-4 w-4" /> Try again with the same key
            </button>
          )}
          <div className="flex gap-2">
            <button type="button" className="erp-btn erp-btn-ghost h-11 flex-1"
              onClick={backToQuestions} disabled={busy}>
              <ArrowLeft className="h-4 w-4" /> Back to the questions
            </button>
            <button type="button"
              className="erp-btn erp-btn-ghost h-11 flex-1 text-red-700 hover:bg-red-50"
              onClick={abandon} disabled={busy}>
              <Trash2 className="h-4 w-4" /> Abandon
            </button>
          </div>
          <p className="text-erp-xs text-muted-foreground">
            Abandoning throws this interview away on this device and sends nothing. It is the
            only path by which an interview disappears without reaching the server.
          </p>
        </div>
      </Panel>
    </div>
  )
}

/**
 * What the sample still needs.
 *
 * Shown before submission and not after, because the useful moment is while the
 * interviewer can still choose a different respondent. A cell that is closed is
 * a 409 waiting to happen when the poll blocks on quota, and the server's
 * refusal names the cell — but hearing it after twenty minutes of interviewing
 * is hearing it too late.
 */
function QuotaPanel({ session }: { session: CaptureSnapshot }) {
  const quota = session.bundle?.quota
  if (!quota || quota.dimensions.length === 0) return null
  return (
    <Panel
      title="Quota"
      description={quota.enforcement === 'BLOCK'
        ? 'This poll BLOCKS on quota — a full cell is refused, not warned about.'
        : quota.enforcement === 'WARN'
          ? 'This poll warns on quota. A full cell is still accepted.'
          : 'Quota is off on this poll.'}
    >
      <div className="space-y-3">
        {quota.dimensions.map((d) => (
          <div key={d.id}>
            <p className="text-erp-xs uppercase tracking-wide text-muted-foreground">{d.name}</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {d.cells.map((c) => (
                <Pill key={c.id} tone={c.openForCapture
                  ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30'
                  : 'bg-muted text-muted-foreground border-border line-through'}>
                  {c.label ?? c.valueCode}
                  {' '}
                  <span className="ml-1 tabular-nums">
                    {c.achieved}/{c.target ?? '—'}
                  </span>
                </Pill>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  )
}

// ── the queue, permanently on screen ────────────────────────────────────────

/**
 * The depth is always visible and nothing is ever dropped silently.
 *
 * A rejected interview stays in a list with the server's sentence attached until
 * a person either retries it or deletes it on purpose. The alternative — quietly
 * discarding a submission the server refused — is a day of fieldwork
 * disappearing with nothing to show a supervisor.
 */
function QueuePanel() {
  const queue = useCaptureQueue()
  const [busy, setBusy] = useState(false)

  const clipsWaiting = queue.waitingClips.reduce((n, w) => n + w.count, 0)

  if (queue.pending.length === 0 && queue.rejected.length === 0
    && queue.waitingClips.length === 0 && !queue.storageBlocked) {
    return null
  }

  return (
    <Panel
      title="On this device"
      description={queue.lastResult ?? 'Interviews that have not reached the server.'}
      right={queue.pending.length > 0 || queue.waitingClips.length > 0 ? (
        <button type="button" className="erp-btn h-9 text-erp-xs" disabled={busy || queue.draining}
          onClick={async () => { setBusy(true); await drainQueue(); setBusy(false) }}>
          {queue.draining || busy
            ? <Loader2 className="h-3 w-3 animate-spin" />
            : <Send className="h-3 w-3" />}
          Send now
        </button>
      ) : undefined}
    >
      <div className="space-y-3">
        {queue.storageBlocked && (
          <Note tone="bad">
            This browser will not let the page store anything — a private window, or site data
            turned off. Interviews still work, but a reload loses the one in progress and the
            queue cannot survive closing the tab. Use a normal window for fieldwork.
          </Note>
        )}

        {queue.pending.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-erp-sm">
              <strong className="tabular-nums">{queue.pending.length}</strong> waiting to be sent.
              {queue.online
                ? ' The device has signal, so this should clear on its own.'
                : ' They go up as soon as there is signal.'}
            </p>
            {queue.pending.map((p) => (
              <div key={p.idempotencyKey} className="text-erp-xs text-muted-foreground">
                {p.surveyRef ?? p.surveyId} · queued {fmtDateTime(p.queuedAt)}
                {p.clipCount > 0 ? ` · ${p.clipCount} clip${p.clipCount === 1 ? '' : 's'} held` : ''}
                {p.attempts > 0 ? ` · ${p.attempts} attempt${p.attempts === 1 ? '' : 's'}` : ''}
                {p.lastProblem ? ` — ${p.lastProblem}` : ''}
              </div>
            ))}
          </div>
        )}

        {/*
          Listed apart from the interviews above, because the interview these
          belong to is already on the server and saying "waiting to be sent"
          about it would be wrong. Only the recording is still going up.
        */}
        {queue.waitingClips.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-erp-sm">
              <strong className="tabular-nums">{clipsWaiting}</strong>
              {clipsWaiting === 1 ? ' recording' : ' recordings'} still going up. The interviews
              they belong to have been accepted — only the audio is outstanding, and each retries
              on its own.
            </p>
            {queue.waitingClips.map((w) => (
              <div key={w.responseId} className="text-erp-xs text-muted-foreground">
                {w.surveyRef ?? w.responseId} · held {fmtDateTime(w.queuedAt)}
                {` · ${w.count} clip${w.count === 1 ? '' : 's'}`}
                {w.attempts > 0 ? ` · ${w.attempts} attempt${w.attempts === 1 ? '' : 's'}` : ''}
                {w.lastProblem ? ` — ${w.lastProblem}` : ''}
              </div>
            ))}
          </div>
        )}

        {queue.rejected.length > 0 && (
          <div className="space-y-2">
            <p className="text-erp-sm text-red-700">
              <strong className="tabular-nums">{queue.rejected.length}</strong> refused by the
              server. They are kept here until you decide what to happen to them.
            </p>
            {queue.rejected.map((r) => (
              <div key={r.idempotencyKey}
                className="rounded-md border border-red-200 bg-red-50/50 p-3 space-y-2">
                <p className="text-erp-xs text-muted-foreground">
                  {r.surveyRef ?? r.surveyId} · {fmtDateTime(r.rejectedAt)}
                </p>
                <p className="text-erp-sm">{r.problem}</p>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="erp-btn h-9 text-erp-xs"
                    onClick={() => retryRejected(r.idempotencyKey)}>
                    <RefreshCw className="h-3 w-3" /> Put it back in the queue
                  </button>
                  <button type="button"
                    className="erp-btn erp-btn-ghost h-9 text-erp-xs text-red-700"
                    onClick={() => discardRejected(r.idempotencyKey)}>
                    <Trash2 className="h-3 w-3" /> Delete it for good
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  )
}

/**
 * A code with no entry in `vocab.tsx`, rendered readably.
 *
 * The register's gender and age-band lists come from the server and `vocab.tsx`
 * carries no labels for them, so the RAW CODE is title-cased rather than mapped
 * through a hand-written table here — a table in this file is exactly the drift
 * that made PollSphere's filters offer values the server had never heard of.
 */
function humanise(code: string): string {
  return code.charAt(0) + code.slice(1).toLowerCase().replace(/_/g, ' ')
}
